
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradingCentralAuthenticationResponse struct
 */
class TradingCentralAuthenticationResponse {
  /**
   * Initialize struct
   * @param {!string=} authorization
   * @constructor
   */
  constructor (authorization = '') {
    this.Authorization = authorization
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradingCentralAuthenticationResponse}
   * @param {!TradingCentralAuthenticationResponse} other Other struct
   * @returns {!TradingCentralAuthenticationResponse} This struct
   */
  copy (other) {
    if (other.Authorization != null) {
      this.Authorization = other.Authorization
    } else {
      this.Authorization = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradingCentralAuthenticationResponse}
   * @returns {!TradingCentralAuthenticationResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradingCentralAuthenticationResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradingCentralAuthenticationResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradingCentralAuthenticationResponse}
   * @param {!TradingCentralAuthenticationResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradingCentralAuthenticationResponse)) {
      throw new TypeError('Instance of TradingCentralAuthenticationResponse is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradingCentralAuthenticationResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Authorization: ((this.Authorization != null) ? this.Authorization : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradingCentralAuthenticationResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradingCentralAuthenticationResponse} other Object value
   * @returns {!TradingCentralAuthenticationResponse} Created struct
   */
  static fromObject (other) {
    return new TradingCentralAuthenticationResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradingCentralAuthenticationResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradingCentralAuthenticationResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradingCentralAuthenticationResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10008
  }
}

export { TradingCentralAuthenticationResponse };

/**
 * Fast Binary Encoding TradingCentralAuthenticationResponse field model
 */
class FieldModelTradingCentralAuthenticationResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Authorization = new fbe.FieldModelString(buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Authorization.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Authorization.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradingCentralAuthenticationResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10008
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Authorization.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Authorization.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Authorization.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @param {!TradingCentralAuthenticationResponse} fbeValue Default value, defaults is new TradingCentralAuthenticationResponse()
   * @returns {!TradingCentralAuthenticationResponse} TradingCentralAuthenticationResponse value
   */
  get (fbeValue = new TradingCentralAuthenticationResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @param {!TradingCentralAuthenticationResponse} fbeValue TradingCentralAuthenticationResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Authorization.fbeSize) <= fbeStructSize) {
      fbeValue.Authorization = this.Authorization.get()
    } else {
      fbeValue.Authorization = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Authorization.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @param {!TradingCentralAuthenticationResponse} fbeValue TradingCentralAuthenticationResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradingCentralAuthenticationResponse}
   * @param {TradingCentralAuthenticationResponse} fbeValue TradingCentralAuthenticationResponse value
   */
  setFields (fbeValue) {
    this.Authorization.set(fbeValue.Authorization)
  }
}

export { FieldModelTradingCentralAuthenticationResponse };

/**
 * Fast Binary Encoding TradingCentralAuthenticationResponse model
 */
class TradingCentralAuthenticationResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradingCentralAuthenticationResponse(this.buffer, 4)
  }

  /**
   * Get the TradingCentralAuthenticationResponse model
   * @this {!TradingCentralAuthenticationResponseModel}
   * @returns {!FieldModelTradingCentralAuthenticationResponse} model TradingCentralAuthenticationResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradingCentralAuthenticationResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradingCentralAuthenticationResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradingCentralAuthenticationResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradingCentralAuthenticationResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradingCentralAuthenticationResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradingCentralAuthenticationResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradingCentralAuthenticationResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradingCentralAuthenticationResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradingCentralAuthenticationResponseModel}
   * @param {!TradingCentralAuthenticationResponse} value TradingCentralAuthenticationResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradingCentralAuthenticationResponseModel}
   * @param {!TradingCentralAuthenticationResponse} value TradingCentralAuthenticationResponse value, defaults is new TradingCentralAuthenticationResponse()
   * @return {!object} Deserialized TradingCentralAuthenticationResponse value and its size
   */
  deserialize (value = new TradingCentralAuthenticationResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradingCentralAuthenticationResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradingCentralAuthenticationResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradingCentralAuthenticationResponseModel}
   * @param {!number} prev Previous TradingCentralAuthenticationResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradingCentralAuthenticationResponseModel };
